import {
  QueriedData,
  showErrorNotification,
  showSuccessNotification,
} from '../assets/useAssets';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useIntl } from 'react-intl';
import {
  Service,
  ServiceWriteModel,
  createService,
  deleteService,
  getAllServices,
  updateService,
} from 'src/api/services';

export interface UseServicesResult {
  servicesQuery: QueriedData<Service[]>;
  createService: (service: ServiceWriteModel) => void;
  updateService: (data: { id: string; service: ServiceWriteModel }) => void;
  archiveService: (serviceID: string) => void;
}

function useCreateService() {
  const queryClient = useQueryClient();
  const intl = useIntl();
  const { mutate: create } = useMutation(
    (service: ServiceWriteModel) => createService(service),
    {
      onSuccess: () => {
        showSuccessNotification('service-create-success-message', intl);
        queryClient.invalidateQueries(['services']);
      },
      onError: () => {
        showErrorNotification('service-create-fail-message', intl);
      },
    },
  );
  return create;
}

export function useServices(): UseServicesResult {
  const query = useQuery({
    queryFn: async () => getAllServices(),
    queryKey: ['services'],
  });

  const create = useCreateService();
  const queryClient = useQueryClient();
  const intl = useIntl();
  const { mutate: archive } = useMutation(
    (serviceID: string) => deleteService(serviceID),
    {
      onSuccess: () => {
        showSuccessNotification('service-archive-success-message', intl);
        queryClient.invalidateQueries(['services']);
      },
      onError: () => showErrorNotification('service-archive-fail-message', intl),
    },
  );
  const updateService = useUpdateService();
  return {
    servicesQuery: query,
    createService: create,
    archiveService: archive,
    updateService,
  };
}

export function useUpdateService() {
  const queryClient = useQueryClient();
  const intl = useIntl();

  const { mutate: update } = useMutation(
    ({ id, service }: { id: string; service: ServiceWriteModel }) =>
      updateService(id, service),
    {
      onSuccess: (_, { id }) => {
        showSuccessNotification('service-update-success-message', intl);
        queryClient.invalidateQueries(['services', id]);
        queryClient.invalidateQueries(['services']);
      },
      onError: () => {
        showErrorNotification('service-update-fail-message', intl);
      },
    },
  );
  return update;
}
